import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface VehicleExtra {
  id: string;
  nameHu: string;
  nameEn: string;
  descriptionHu: string;
  descriptionEn: string;
  price: number;
  currency: 'HUF' | 'EUR' | 'USD';
  taxRate: number; // Százalékban (pl. 27 = 27%)
  maxCost: number; // Maximum költség (0 = nincs limit)
  perDay: boolean; // true = naponta, false = egyszeri
  allowedQuantity: number; // Maximális mennyiség
  imageUrl?: string;
  order: number;
  active: boolean;
}

const ExtraManager: React.FC = () => {
  const [extras, setExtras] = useState<VehicleExtra[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingExtra, setDeletingExtra] = useState<VehicleExtra | null>(null);
  const [formData, setFormData] = useState<Partial<VehicleExtra>>({
    nameHu: '',
    nameEn: '',
    descriptionHu: '',
    descriptionEn: '',
    price: 0,
    currency: 'HUF',
    taxRate: 27,
    maxCost: 0,
    perDay: true,
    allowedQuantity: 1,
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchExtras();
  }, []);

  const fetchExtras = async () => {
    try {
      const q = query(collection(db, 'vehicleExtras'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as VehicleExtra[];
      setExtras(data);
    } catch (error) {
      console.error('Error fetching extras:', error);
      toast.error('Hiba az extrák betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await updateDoc(doc(db, 'vehicleExtras', editingId), {
          ...formData,
          updatedAt: new Date()
        });
        toast.success('Extra frissítve');
      } else {
        await addDoc(collection(db, 'vehicleExtras'), {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        toast.success('Extra létrehozva');
      }
      
      setFormData({
        nameHu: '',
        nameEn: '',
        descriptionHu: '',
        descriptionEn: '',
        price: 0,
        currency: 'HUF',
        taxRate: 27,
        maxCost: 0,
        perDay: true,
        allowedQuantity: 1,
        order: 0,
        active: true
      });
      setEditingId(null);
      fetchExtras();
    } catch (error) {
      console.error('Error saving extra:', error);
      toast.error('Hiba a mentés során');
    }
  };

  const handleEdit = (extra: VehicleExtra) => {
    setFormData(extra);
    setEditingId(extra.id);
  };

  const handleDeleteClick = (extra: VehicleExtra) => {
    setDeletingExtra(extra);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingExtra) return;
    
    try {
      await deleteDoc(doc(db, 'vehicleExtras', deletingExtra.id));
      toast.success('Extra törölve');
      fetchExtras();
    } catch (error) {
      console.error('Error deleting extra:', error);
      toast.error('Hiba a törlés során');
    } finally {
      setDeletingExtra(null);
    }
  };

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      await updateDoc(doc(db, 'vehicleExtras', id), {
        active: !currentActive,
        updatedAt: new Date()
      });
      toast.success(currentActive ? 'Extra inaktiválva' : 'Extra aktiválva');
      fetchExtras();
    } catch (error) {
      console.error('Error toggling extra:', error);
      toast.error('Hiba a művelet során');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Betöltés...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {editingId ? 'Extra szerkesztése' : 'Új extra'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Név (Magyar) *
              </label>
              <input
                type="text"
                required
                value={formData.nameHu}
                onChange={(e) => setFormData({ ...formData, nameHu: e.target.value })}
                placeholder="pl. Gyerekülés"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Név (Angol) *
              </label>
              <input
                type="text"
                required
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                placeholder="e.g. Child seat"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Leírás (Magyar)
              </label>
              <input
                type="text"
                value={formData.descriptionHu}
                onChange={(e) => setFormData({ ...formData, descriptionHu: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Leírás (Angol)
              </label>
              <input
                type="text"
                value={formData.descriptionEn}
                onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ár *
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
                />
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value as 'HUF' | 'EUR' | 'USD' })}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
                >
                  <option value="HUF">HUF</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                ÁFA (%) *
              </label>
              <input
                type="number"
                required
                min="0"
                max="100"
                value={formData.taxRate}
                onChange={(e) => setFormData({ ...formData, taxRate: parseFloat(e.target.value) })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Maximum költség (0 = nincs limit)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.maxCost}
                onChange={(e) => setFormData({ ...formData, maxCost: parseFloat(e.target.value) })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Maximális mennyiség *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.allowedQuantity}
                onChange={(e) => setFormData({ ...formData, allowedQuantity: parseInt(e.target.value) })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sorrend *
              </label>
              <input
                type="number"
                required
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.perDay}
                  onChange={(e) => setFormData({ ...formData, perDay: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Naponta számítva</span>
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
            >
              {editingId ? 'Frissítés' : 'Létrehozás'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setFormData({
                    nameHu: '',
                    nameEn: '',
                    descriptionHu: '',
                    descriptionEn: '',
                    price: 0,
                    currency: 'HUF',
                    taxRate: 27,
                    maxCost: 0,
                    perDay: true,
                    allowedQuantity: 1,
                    order: 0,
                    active: true
                  });
                }}
                className="bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white px-4 py-2 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Mégse
              </button>
            )}
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Sorrend
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Név (HU / EN)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Ár
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                ÁFA
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Típus
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Max. db
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Státusz
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Műveletek
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {extras.map((extra) => (
              <tr key={extra.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {extra.order}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                  <div>{extra.nameHu}</div>
                  <div className="text-gray-500 dark:text-gray-400">{extra.nameEn}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {extra.price.toLocaleString()} {extra.currency}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {extra.taxRate}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {extra.perDay ? 'Naponta' : 'Egyszeri'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {extra.allowedQuantity}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => toggleActive(extra.id, extra.active)}
                    className={`px-2 py-1 text-xs rounded-full ${
                      extra.active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {extra.active ? 'Aktív' : 'Inaktív'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEdit(extra)}
                    className="text-primary-600 hover:text-primary-900 dark:text-primary-400 mr-3"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(extra)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!deletingExtra}
        onClose={() => setDeletingExtra(null)}
        onConfirm={handleDeleteConfirm}
        title="Extra törlése"
        deleteMessage={`Biztosan törölni szeretnéd a(z) "${deletingExtra?.nameHu}" extrát?`}
        deleteWarning="Ez a művelet nem vonható vissza. Az extra véglegesen törlődik."
        danger={true}
      />
    </div>
  );
};

export default ExtraManager;
